/**
 * 中文 — Mandarin Chinese (Simplified) translations for the Farmsons site.
 * `data` overrides content from src/lib/data.js, keyed by slug / name / index.
 * Cultivar, rootstock and series names stay in Latin script — they are the
 * identifiers used in international horticultural trade.
 */
export default {
  common: {
    quote: "索取报价",
    downloadProfile: "下载公司简介",
  },
  nav: {
    ventures: "我们的业务",
    fruit: "水果贸易",
    rootstock: "砧木进口与分销",
    mango: "芒果出口",
    story: "我们的故事",
    sourcing: "采购网络",
    partners: "合作伙伴",
    quality: "品质保障",
    insights: "行业洞察",
    contact: "联系我们",
    language: "语言",
  },
  hero: {
    lines: ["从果园", "到出口 —", "贸易驱动", "农产品未来"],
    sub: "Farmsons 将喜马偕尔、克什米尔及印度芒果产区的种植者直接对接国际买家——以现代化的贸易公司架构，整合规范运营、质量标准与真实的种植户合作关系。",
    partner: "与我们合作",
  },
  marquee: {
    items: ["苹果", "芒果", "砧木", "全球进口", "出口等级", "喜马偕尔", "克什米尔", "马尔达"],
  },
  ventures: {
    overline: "我们的业务",
    heading: "三大业务板块，同一运营标准。",
    para: "一个可复制的模式，无需重新设计即可拓展新业务——从今天的贸易，迈向未来的进口与出口。",
    explore: "了解",
  },
  cine: { l1: "从果园", l2pre: "到", l2accent: "出口。" },
  partnersGrid: {
    overline: "合作伙伴",
    heading: "深受供应链各环节合作伙伴的信赖。",
    visit: "访问网站",
  },
  home: {
    storyOverline: "我们的故事",
    storyHeading: "世代传承的根基，融合现代化运营体系。",
    storyLink: "阅读我们的完整故事并认识创始人",
    ctaHeading: "让我们共建贸易伙伴关系。",
  },
  about: {
    overline: "我们的故事",
    title: "世代传承的根基，融合现代化运营体系。",
    lede: "一家扎根加尔各答的贸易公司，正开启新的篇章——将规范架构、质量标准与种植者直接合作关系汇聚于一家企业。",
    founderOverline: "创始人",
    founderHeading: "以不同背景，投身传统行业。",
    exploreLink: "了解我们的业务——水果贸易、砧木与芒果出口",
  },
  vertical: {
    overline: "我们的业务",
    ctaHeading: "对{venture}感兴趣？欢迎洽谈。",
    exploreOthers: "探索其他业务：",
    sourcingLink: "采购与产区",
    appleOverline: "苹果品种",
    appleTitle: "来自喜马偕尔邦与克什米尔的苹果",
    appleIntro: "立足于我们真实采购的品种——从喜马偕尔的招牌 Royal Delicious 到酸爽的青苹果 Granny Smith 和高海拔的 Kinnaur 苹果。",
    mangoOverline: "芒果品种",
    mangoTitle: "来自印度南部与东部的芒果",
    mangoIntro: "从优质的 Alphonso、获得地理标志认证的 Kesar 和 Banganapalli，到孟加拉马尔达产区芬芳的 Himsagar 与 Langra。",
  },
  sourcing: {
    overline: "采购与产区",
    title: "从喜马拉雅山坡到恒河三角洲——以及八大进口来源国。",
    lede: "我们的每个采购产区都经过精心选择。将鼠标悬停在地图上的任一节点，即可探索我们全球种植户关系与贸易来源网络。",
    legendHQ: "加尔各答总部",
    legendOrigins: "采购与贸易来源——悬停节点即可探索。",
    importOverline: "苹果进口来源国",
    tradeLink: "了解我们的水果贸易与进口方式",
  },
  map: { title: "全球采购网络" },
  partnersPage: {
    overline: "合作伙伴",
    title: "深受供应链各环节合作伙伴的信赖。",
    lede: "从果园投入品到分销与出口物流，Farmsons 与知名企业携手合作，确保每个环节的可靠交付。",
  },
  qualityPage: {
    overline: "品质与认证",
    title: "为跨境信任而生的标准。",
    lede: "国际买家以稳定性来评估贸易伙伴。四大支柱支撑 Farmsons 的每一批货物——从具名种植户到遥远港口。",
  },
  qualityGrid: {
    overline: "为何重要",
    para: "国际买家以稳定性来评估贸易伙伴。我们的质量框架从第一天起就围绕进口规格、合规与可追溯性构建。",
    badge: "出口就绪物流",
  },
  insights: {
    overline: "行业洞察",
    title: "从果园到港口的笔记。",
    lede: "关于印度水果贸易的实用视角——为买家与种植者提供产季展望、农艺知识与出口经验。",
    read: "阅读全文",
  },
  article: {
    back: "全部洞察",
    cta: "希望与 Farmsons 采购或合作？",
  },
  contact: {
    overline: "联系 / 索取报价",
    title: "让我们共建贸易伙伴关系。",
    para: "无论您是国际进口商、果园种植者还是供应合作伙伴——告诉我们您的需求，我们的团队将直接回复。",
    address: "印度西孟加拉邦加尔各答",
    waPreface: "希望快速沟通？通过 WhatsApp 联系我们。",
    profileTitle: "公司简介",
    profileDesc: "一页了解 Farmsons——我们的业务板块、采购产区与质量标准。",
  },
  form: {
    name: "姓名",
    email: "电子邮箱",
    company: "公司",
    country: "国家",
    interest: "产品意向",
    message: "留言",
    namePh: "您的姓名",
    emailPh: "you@company.com",
    companyPh: "公司名称",
    countryPh: "国家",
    interestPrompt: "请选择意向",
    messagePh: "请告诉我们您的需求、货量与时间计划。",
    submit: "发送询盘",
    sending: "发送中...",
    incomplete: "请填写所有字段。",
    error: "出现问题，请重试。",
    tooMany: "请求过于频繁，请稍后再试。",
    successTitle: "询盘已收到",
    successBody: "谢谢。Farmsons 团队成员将尽快与您联系。",
    successAgain: "再发一条询盘",
    successToast: "询盘已发送——我们的团队将与您联系。",
  },
  wa: {
    chat: "WhatsApp 洽谈",
    aria: "通过 WhatsApp 与 Farmsons 联系",
    message: "您好 Farmsons，我想咨询农产品贸易合作事宜。",
  },
  rootstockGuide: {
    overline: "砧木品种指南",
    title: "为您的果园选择合适的砧木。",
    intro: "砧木的选择决定树体大小、果园结果早晚、抗病能力以及对当地土壤气候的适应性。Farmsons 帮助喜马偕尔与克什米尔的种植者将合适的砧木匹配到合适的地块——而不只是供应。请在下方按系列或树势类别筛选。",
    series: "系列",
    vigor: "树势",
    all: "全部",
    bestFit: "最适于",
    noMatch: "没有符合该组合的砧木。",
  },
  unsub: {
    title: "退订",
    processing: "正在处理您的请求…",
    success: "已退订",
    successBody: "您已从我们的通讯名单中移除。期待后会有期。",
    invalid: "链接无效",
    invalidBody: "此退订链接无效或已被使用。",
    home: "返回首页",
  },
  footer: {
    blurb: "从果园到出口——一家注册农产品贸易公司，在种植者与国际买家之间构建现代化基础设施。创立于 1985 年，加尔各答。",
    explore: "探索",
    office: "办公室",
    ourStory: "我们的故事",
    city: "西孟加拉邦加尔各答",
    country: "印度",
    rights: "版权所有。",
    tagline: "贸易驱动农产品未来。",
    newsTitle: "Farmsons 订阅通讯",
    newsDesc: "关于印度水果贸易的产季笔记，直达您的邮箱。",
    newsPlaceholder: "您的电子邮箱",
    newsButton: "订阅",
    newsSuccess: "订阅成功——欢迎加入 Farmsons 通讯。",
    newsError: "订阅失败，请重试。",
  },
  data: {
    ventures: {
      "fruit-trading": {
        status: "正在进行",
        title: "水果贸易",
        tagline: "苹果与芒果采购、结构化进口及佣金制分销——立足加尔各答。",
        tags: ["苹果", "芒果", "进口", "分销"],
        intro: "水果贸易是 Farmsons 当前活跃并创造收入的业务板块。我们直接向种植者采购，实施出口级质量控制，并通过透明、以关系为先的供应链，将水果送达国内外买家。",
        sections: [
          { heading: "国产苹果采购", body: "我们直接从喜马偕尔邦（Shimla、Kinnaur、Kullu）和克什米尔（Shopian、Sopore）的果园采购苹果。在果园地头采购——而非通过匿名的曼迪市场批次——意味着种植者获得更好的价格，买家获得完整的可追溯性。" },
          { heading: "印度南部与东部的芒果采购", body: "产季期间，我们从恒河三角洲西孟加拉邦的 Malda 与 Murshidabad 产区，以及安得拉邦和卡纳塔克邦的成熟果园采购芒果，按每位买家的需求匹配品种与等级。" },
          { heading: "结构化苹果进口", body: "Farmsons 参与印度从八个来源国——土耳其、伊朗、意大利、波兰、智利、南非、新西兰和美国——的苹果进口贸易，管理进入印度市场的采购、单证与冷链。" },
          { heading: "佣金制分销", body: "通过立足加尔各答的佣金制分销合作，我们将货物接入可靠的批发商与零售商网络，为供应商提供稳定的消化能力与市场覆盖。" },
        ],
        highlights: {
          title: "一批货物的旅程",
          items: [
            { title: "01 · 采购", desc: "与具名种植户关系绑定的地头采购。" },
            { title: "02 · 分级", desc: "按规格校准果径、糖度与瑕疵分级。" },
            { title: "03 · 运输", desc: "温控处理直至市场或港口。" },
            { title: "04 · 结算", desc: "为种植者与买家提供透明及时的结算。" },
          ],
        },
      },
      rootstock: {
        status: "即将推出",
        title: "砧木进口与分销",
        tagline: "经认证的苹果砧木——M9、MM106、M111——面向喜马偕尔与克什米尔的高密度果园。",
        intro: "砧木进口与分销业务使 Farmsons 成为值得信赖的农资合作伙伴。我们进口经认证的苹果无性系砧木，分销给正在进行果园现代化改造的种植者——将合适的砧木匹配到合适的地块。",
        sections: [
          { heading: "什么是砧木——为何重要", body: "苹果树是两种植物的结合：结果品种（接穗）嫁接在根系（砧木）之上。砧木控制树体大小、结果早晚以及对土壤和病害的耐受能力。选对砧木是现代化果园建设中最重要的决策。" },
          { heading: "M9——高密度，早结果", body: "一种矮化砧木，可保持树体矮小，配合格架每公顷可栽数千株。果园结果更早，修剪与采收更省力——高密度栽培的基础。" },
          { heading: "MM106——均衡的主力", body: "半矮化砧木，在树势、固地与产量之间取得良好平衡。非常适合尚未完全配套高密度设施的各类地块。" },
          { heading: "M111——适应艰难地块的强树势", body: "树势强健、适应性强的砧木，耐旱、耐瘠薄土壤，在边缘地或重茬地上也能长成固地良好的树体，而矮化类型在这些地块难以生长。" },
        ],
        highlights: {
          title: "种植者为何与我们合作",
          items: [
            { title: "认证苗木", desc: "可追溯、经病害筛查的无性系材料。" },
            { title: "精准建议", desc: "按地块、品种与目标匹配砧木。" },
            { title: "高密度就绪", desc: "支持现代化格架果园系统。" },
            { title: "供应可靠", desc: "结构化进口，不错过栽植季节。" },
          ],
        },
      },
      "mango-export": {
        status: "即将推出",
        title: "芒果出口",
        tagline: "Alphonso、Kesar、Banganapalli 与 Totapuri——出口等级，销往阿联酋、英国、美国与加拿大。",
        intro: "芒果出口将 Farmsons 的采购实力延伸至国际市场。依托可追溯采购与出口级标准，我们正在构建通路，让印度优质芒果品种抵达海外挑剔的买家。",
        sections: [
          { heading: "Alphonso——旗舰品种", body: "Alphonso 以藏红花色的果肉、馥郁香气与浓郁风味著称，是印度最负盛名的芒果，也是海湾地区、英国与北美高端出口需求的基石。" },
          { heading: "Kesar——芳香鲜亮", body: "以亮橙色果肉与独特香气闻名，Kesar 耐运输，在南亚侨民市场拥有忠实的拥趸。" },
          { heading: "Banganapalli 与 Totapuri——南印货量", body: "Banganapalli 果实个大、无纤维、味甜，货架期长；Totapuri 则补充了适合加工的酸味货量——两者共同将出口篮子扩展到单一产季之外。" },
        ],
        highlights: {
          title: "目标市场",
          items: [
            { title: "阿联酋", desc: "海湾地区对印度优质芒果的成熟需求。" },
            { title: "英国", desc: "侨民与专业零售的强劲拉动。" },
            { title: "美国", desc: "出口协议下不断增长的高端市场。" },
            { title: "加拿大", desc: "对可追溯、分级果实的需求不断扩大。" },
          ],
        },
      },
    },
    pillars: [
      { title: "1985 年以来的传承", body: "Farmsons 承载着近四十年的家族农产品贸易传承——扎根于喜马偕尔邦、克什米尔以及印度南部和东部芒果产区与种植者的真实合作关系。" },
      { title: "我们填补的空白", body: "印度农产品贸易长期依靠非正式关系与本地曼迪市场运转，几乎没有连接种植者与国际买家的现代化基础设施。Farmsons 正在构建这一层——从水果贸易，到砧木分销与芒果出口。" },
      { title: "根基与规范相遇", body: "规范架构、质量标准与种植者直接合作关系，汇聚于一家企业。世代传承的根基与现代化运营规范相遇——一家扎根加尔各答的贸易公司正开启新的篇章。" },
    ],
    founder: {
      name: "Zahid Shamim",
      role: "创始人",
      tags: ["贾达夫普尔大学", "UCLA Anderson MBA", "曾任职 Visa", "曾任职 PropTech"],
      paras: [
        "Farmsons 正由创始人 Zahid Shamim 带领迈入新阶段——他是贾达夫普尔大学计算机科学工程专业毕业生、UCLA Anderson 的 MBA，曾在 Visa 和一家 PropTech 公司担任产品与技术领导职务。",
        "他目前领导 Farmsons 的战略、运营、产品与市场化——将全球商业规范应用于这家总部位于加尔各答的农产品贸易公司。这是一个世代根基与现代运营规范相遇的故事：扎根加尔各答的教育背景、全球化的商业生涯，以及回归家族企业、带领其开启新篇章的历程。",
        "他的愿景是将这门以关系驱动的贸易生意转变为现代化的专业基础设施——种植者是具名合作伙伴，品质按国际规格衡量，世界各地的买家都能与一家可问责的企业交易。技术、透明与可追溯是贯穿始终的主线——从今天的水果贸易，走向砧木分销与芒果出口。",
      ],
    },
    regions: [
      { name: "喜马偕尔邦", type: "苹果产区", note: "Shimla、Kinnaur、Kullu 与 Mandi 的高海拔果园。" },
      { name: "克什米尔", type: "苹果产区", note: "来自 Shopian 与 Sopore 的优质山谷苹果。" },
      { name: "Malda 与 Murshidabad", type: "芒果产区——西孟加拉邦", note: "以芬芳芒果品种闻名的恒河三角洲产区。" },
      { name: "安得拉与卡纳塔克", type: "芒果产区——南印度", note: "来自南方成熟果园的 Banganapalli 与 Totapuri。" },
    ],
    importOrigins: ["土耳其", "伊朗", "意大利", "波兰", "智利", "南非", "新西兰", "美国"],
    mapLabels: ["加尔各答（总部）", "喜马偕尔与克什米尔", "土耳其", "伊朗", "意大利", "波兰", "美国", "智利", "南非", "新西兰"],
    quality: [
      { title: "可追溯采购", desc: "每批货物都与具名种植户关系和特定产区绑定——绝不来自匿名的曼迪市场批次。这种可追溯性让买家对产地有信心，也让我们对品质有把握。" },
      { title: "出口级分级", desc: "水果按国际买家规格分级——校准果径、糖度（brix）测量与瑕疵筛选——确保订购的货物与送达的完全一致，批批如此。" },
      { title: "冷链完整性", desc: "从包装厂到港口的温控处理保护果实硬度、风味与货架期，减少损耗，让远方买家在到货时获得可靠一致的品质。" },
      { title: "合规就绪", desc: "单证与植物检疫流程从第一天起即为跨境贸易而构建，确保货物顺利清关，买家在边境不会遇到意外。" },
    ],
    interests: ["苹果贸易", "芒果出口", "苹果进口", "砧木供应", "分销合作", "其他"],
    vigor: { Dwarfing: "矮化", "Semi-dwarf": "半矮化", "Semi-vigorous": "半乔化", Vigorous: "乔化" },
    varieties: {
      "Royal Delicious": { tag: "喜马偕尔 · 九月高峰", desc: "喜马偕尔的招牌苹果——深红、脆甜。主要采自 Kullu、Shimla、Kinnaur 与 Mandi。" },
      "Red Delicious": { tag: "喜马偕尔 · 九月", desc: "最古老的商业品种之一——风味温和，深红色果皮带有细密绿纹。" },
      "Golden Delicious": { tag: "查谟-克什米尔 · 十月", desc: "黄绿色果皮，带有温和的蜂蜜般甜香。在查谟与克什米尔广泛种植。" },
      "Royal Gala": { tag: "克什米尔 · 八月", desc: "浅红至粉红，甜脆可口——采自克什米尔的早季宠儿。" },
      Fuji: { tag: "高海拔喜马偕尔 · 十月", desc: "个大、紧实、极甜，货架期长。种植于喜马偕尔特选的高海拔果园。" },
      "Granny Smith": { tag: "特选产区 · 十月", desc: "翠绿清新、酸爽宜人——在特选产区种植的紧实多用品种。" },
      "Kinnaur Apple": { tag: "Kinnaur · 约 9000 英尺", desc: "生长于约 9000 英尺的高海拔——深浓的暗红色，以格外脆爽著称。" },
      "Scarlet Spur": { tag: "喜马偕尔 · 九月", desc: "Red Delicious 的芽变选种——鲜红果皮配甜白果肉。" },
      Alphonso: { tag: "优质 · 四至六月", desc: "印度最负盛名的芒果——藏红花色果肉、馥郁香气，其风味定义了高端出口需求。" },
      Kesar: { tag: "地理标志 · 古吉拉特", desc: "获地理标志认证，产自古吉拉特 Junagadh / Gir Somnath 地区——亮橙色果肉与独特香气。" },
      Banganapalli: { tag: "地理标志 · 安得拉", desc: "获地理标志认证，产自安得拉邦——个大、无纤维、味甜，货架期长。" },
      Totapuri: { tag: "卡纳塔克与安得拉", desc: "酸而紧实，带独特的尖喙外形——在卡纳塔克与安得拉广泛种植。" },
      Himsagar: { tag: "Malda · 西孟加拉", desc: "来自西孟加拉 Malda / Murshidabad 产区的芬芳无纤维品种，浓郁细腻。" },
      Langra: { tag: "Malda · 西孟加拉", desc: "来自 Malda / Murshidabad 产区的晚熟芳香绿皮品种。" },
    },
    rootstocks: {
      M9: { traits: "约为标准树体的 30–35%，早果，产量极高。需设立支架。", fit: "灌溉肥沃土壤上的高密度栽植。" },
      M26: { traits: "约为标准树体的 40–50%，早果丰产，抗病性良好。", fit: "中高密度果园。" },
      M7: { traits: "定植后产量中等。", fit: "中低密度栽植。" },
      MM106: { traits: "强健、适应性广，固地性好，抗根部病害。", fit: "喜马偕尔与克什米尔中密度（650–800 株/公顷）传统园。" },
      MM111: { traits: "高度耐旱，根系广展，抗苹果绵蚜；耐火疫病及冠腐/根腐。", fit: "易旱山地。" },
      "EMLA-9": { traits: "经脱毒检测的 M9 选系，表现可靠。", fit: "灌溉深厚土壤上的高密度栽植。" },
      "EMLA-111": { traits: "经脱毒检测的 MM111 选系。", fit: "易旱种植区。" },
      "EMLA-106": { traits: "经脱毒检测的 MM106 选系。", fit: "坡地、黏粒较少的土壤。" },
      "EMLA-7": { traits: "经脱毒检测的 M7 选系。", fit: "坡地。" },
      "B9 (Bud 9)": { traits: "树体与 M9 相近，耐寒性显著更强。", fit: "喜马偕尔与克什米尔的高海拔果园区块。" },
      B118: { traits: "极耐寒，早果且丰产；抗冠腐与根腐。", fit: "土壤多样的寒冷地块。" },
      G11: { traits: "M9 树体级别，抗病性强。", fit: "高密度、病害压力大的地块。" },
      G41: { traits: "强抗重茬病、火疫病与苹果绵蚜。", fit: "在多种土壤上表现良好。" },
      G935: { traits: "略大于 M26，早果丰产，果个大。", fit: "丰产的中密度果园。" },
      G214: { traits: "中等树势，产量与长势俱佳。", fit: "希望树势强于矮化选项的种植者。" },
      G969: { traits: "中等树势，产量与长势俱佳。", fit: "希望树势强于矮化选项的种植者。" },
    },
    articles: {
      "himachal-apple-season-outlook": {
        title: "喜马偕尔苹果产季展望：买家须知",
        excerpt: "从八月的 Gala 到十月的 Fuji，喜马偕尔的采收日历决定了整个产季的供应、价格与品质。",
        readTime: "阅读约 5 分钟",
        body: [
          { h: "随海拔变化的产季", p: "喜马偕尔的苹果采收呈波段展开。Royal Gala 于八月率先上市，Delicious 系列（Royal、Red 与 Scarlet Spur）在九月达到高峰，而 Fuji 和 Granny Smith 等较耐储品种在十月收尾。Kinnaur 的高海拔果园成熟更晚，口感格外脆爽。" },
          { h: "时机为何对买家重要", p: "掌握日历让买家能够规划货量与价格。早季 Gala 享有新鲜溢价；九月 Delicious 高峰提供货量与色泽的最佳平衡；晚季 Fuji 适合需要更长货架期以供应远端市场的买家。" },
          { h: "Farmsons 如何采购", p: "我们在 Shimla、Kullu、Kinnaur 和 Mandi 的果园地头直接采购，将每个批次与具名种植户绑定，并按买家规格分级——确保发出的果实与订购的完全一致。" },
        ],
      },
      "choosing-apple-rootstock-himachal-kashmir": {
        title: "如何为喜马偕尔与克什米尔选择合适的苹果砧木",
        excerpt: "砧木决定树体大小、果园结果早晚，以及对当地土壤与气候的适应能力。以下是选择方法。",
        readTime: "阅读约 6 分钟",
        body: [
          { h: "砧木是根基", p: "苹果树是嫁接在砧木上的结果品种。砧木控制树势（树体大小）、早果性（结果早晚）、抗病性与土壤耐受性——是建园时最重要的决策。" },
          { h: "因地制宜选配砧木", p: "M9、B9、G41 等矮化砧木适合灌溉的高密度果园；B9 为高海拔增加耐寒性。生长势强的 MM111 可应对易旱山坡。MM106 仍是喜马偕尔与克什米尔中密度果园的可靠主力。" },
          { h: "是合作伙伴，不只是供应商", p: "Farmsons 帮助种植者根据土壤、品种与目标选配砧木——将经认证、脱毒检测的苗木与农艺指导送到果园。" },
        ],
      },
      "indian-mango-export-varieties": {
        title: "印度出口的芒果：Alphonso、Kesar 及更多",
        excerpt: "驱动印度优质芒果出口需求的品种指南——及其背后的产区与产季。",
        readTime: "阅读约 4 分钟",
        body: [
          { h: "旗舰品种", p: "Alphonso 以香气与风味领先，Kesar（地理标志，古吉拉特）带来鲜亮色泽，Banganapalli（地理标志，安得拉）则提供个大、无纤维、货架期长的果实。Totapuri 增加酸度货量，而 Himsagar 与 Langra 承载着孟加拉马尔达产区的芬芳。" },
          { h: "产季与市场", p: "旺季为四月至六月。高端需求集中在阿联酋、英国、美国和加拿大，这些市场以可追溯采购与出口级处理为先决条件。" },
          { h: "为出口而建", p: "Farmsons 正在构建以可追溯采购与出口级分级为支撑的通路，让印度优质芒果以稳定一致的状态抵达。" },
        ],
      },
      "apple-import-origins-compared": {
        title: "苹果进口来源国比较：八个国家，一条供应链",
        excerpt: "土耳其、伊朗、意大利、波兰、智利、南非、新西兰与美国各自填补印度苹果进口日历中的不同窗口。以下是比较。",
        readTime: "阅读约 6 分钟",
        body: [
          { h: "印度为何进口苹果", p: "喜马偕尔与克什米尔的国产水果具有季节性。进口填补空缺——延长全年供应，补充本地果园不具备的品种与规格，并在国产库存不足时满足高端零售需求。" },
          { h: "北半球来源国", p: "土耳其、伊朗、意大利与波兰在其秋季采收期间及之后发货。意大利与波兰以稳定的分级和冷库计划著称，可供应至新年；土耳其与伊朗则提供邻近且具竞争力的货量。" },
          { h: "南半球来源国", p: "智利、南非与新西兰按相反的日历采收，在北半球库存老化时到货——让新鲜硬实的果实在印度淡季持续流通。新西兰与智利以食用品质与可靠的出口规范著称。" },
          { h: "美国", p: "美国苹果（尤其是华盛顿州）带来知名的高端品种与严格分级，服务市场高端。" },
          { h: "Farmsons 如何管理", p: "我们协调八大来源国进入印度市场的采购、单证与冷链——让买家从单一负责的合作伙伴处获得稳定品质与全年供应。" },
        ],
      },
    },
  },
};
